<script lang="ts" setup>
import type { Product, Value, Variant } from '@scayle/storefront-nuxt'
import { getFirstAttributeValue } from '@scayle/storefront-nuxt'
import { computed } from 'vue'
import SFProductImage from '~/components/product/SFProductImage.vue'
import { SFHeadline, SFSkeletonLoader } from '#storefront-ui/components'
import SFProductPrice from '~/components/product/SFProductPrice.vue'
import { useFormat, useProductBaseInfo } from '~/composables'
import { ProductColor, SHOE_SIZES_ATTRIBUTES_KEYS } from '~/constants'

const {
  hasBorder = false,
  product,
  variant,
  tab,
  hasColorBox = false,
  hasColour = true,
} = defineProps<{
  hasBorder?: boolean
  product: Product
  variant: Variant
  tab: string
  hasColorBox?: boolean
  hasColour?: boolean
}>()

const { isShoeProduct } = useProductBaseInfo(() => product)
const { formatShoeSize } = useFormat()

const getFormattedShoeSize = (variant: Variant, tab: string) => {
  return formatShoeSize(
    getFirstAttributeValue(
      variant.attributes,
      SHOE_SIZES_ATTRIBUTES_KEYS[
        tab as keyof typeof SHOE_SIZES_ATTRIBUTES_KEYS
      ],
    )?.label || '',
  )
}

const getVariantSizeLabel = (variant: Variant, tab: string) => {
  if (isShoeProduct.value) {
    return getFormattedShoeSize(variant, tab)
  }
  return getFirstAttributeValue(variant.attributes, 'sizeOther')?.label || ''
}

const getSize = computed(() => getVariantSizeLabel(variant, tab))
const getImage = computed(() => product.images[0].hash)
const getColor = computed(
  () => (product.attributes?.color?.values as Value)?.label,
)
const getName = computed(
  () => (product.attributes?.name?.values as Value)?.label,
)
const getBrand = computed(
  () => (product.attributes?.brand?.values as Value)?.label,
)

const hasMixedColors = computed(() => Array.isArray(colorCode.value))

const colorCode = computed(() => {
  const colourName = getColor.value.replace(/-/g, '').trim()
  return ProductColor[colourName]
})
</script>

<template>
  <div :class="{ 'border border-gray-300': hasBorder }" class="flex">
    <div class="flex-1">
      <SFSkeletonLoader v-if="!product" class="h-52 w-full" type="custom" />
      <SFProductImage v-else :alt="getBrand" :image="getImage" />
    </div>
    <div class="flex flex-1 items-center justify-center py-2">
      <div class="flex flex-col justify-between space-y-2 px-4">
        <SFHeadline
          class="!font-normal uppercase text-gray-600"
          size="md"
          tag="h6"
        >
          {{ getBrand }}
        </SFHeadline>
        <SFHeadline class="!font-normal text-gray-900" size="lg" tag="h5">
          {{ getName }}
        </SFHeadline>
        <SFProductPrice :price="variant.price" />
        <div v-if="hasColour" class="flex items-center space-x-2 text-md">
          <span class="capitalize">{{ $t('global.colour') }}:&nbsp;&nbsp;</span>
          <div
            class="relative flex size-4 items-center justify-center overflow-hidden rounded-full border"
          >
            <span
              v-if="hasMixedColors && hasColorBox"
              class="absolute grid size-full grid-cols-2 overflow-hidden rounded-lg lg:group-hover:border lg:group-hover:border-black lg:group-hover:outline lg:group-hover:outline-2 lg:group-hover:outline-offset-[-3px] lg:group-hover:outline-white"
            >
              <span
                v-for="(hex, index) in colorCode"
                :key="`${index}-${hex}`"
                :style="{ backgroundColor: hex }"
                class="size-full"
              />
            </span>
            <span
              v-else
              :style="{ backgroundColor: `${colorCode}` }"
              class="absolute size-full rounded-lg lg:group-hover:border lg:group-hover:border-black lg:group-hover:outline lg:group-hover:outline-2 lg:group-hover:outline-offset-[-3px] lg:group-hover:outline-white"
            />
          </div>
          <span class="font-bold capitalize">{{ getColor }}</span>
        </div>
        <div class="flex items-center space-x-2 text-md">
          <span class="capitalize">{{ $t('global.size') }}:&nbsp;</span>
          <span class="font-bold">{{ tab }} {{ getSize }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
